import React, { useState, useEffect } from 'react'
import { ArrowRight, Play, Brain, Target, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const steps = [
  {
    icon: <Target className="w-6 h-6" />,
    title: "Choose Your Tool",
    description: "Select from our collection of specialized decision wheels or AI analysis tools",
    color: "text-blue-600"
  },
  {
    icon: <Brain className="w-6 h-6" />,
    title: "Input Your Options",
    description: "Add your decision options or describe your situation for AI analysis",
    color: "text-green-600"
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: "Get Instant Results",
    description: "Spin the wheel or receive AI-powered insights in seconds",
    color: "text-purple-600"
  }
]

export const QuickStartGuide: React.FC = () => {
  const navigate = useNavigate()
  const [abTestPath, setAbTestPath] = useState<string>('')

  // AB Test logic - assign user to a test group
  useEffect(() => {
    const storedPath = localStorage.getItem('abTestPath')
    if (storedPath) {
      setAbTestPath(storedPath)
    } else {
      // Randomly assign user to one of two paths
      const paths = ['/tools/yesno-picker', '/decision-analysis/pros-cons']
      const randomPath = paths[Math.floor(Math.random() * paths.length)]
      localStorage.setItem('abTestPath', randomPath)
      setAbTestPath(randomPath)
      
      // Track AB test assignment
      console.log('AB Test Assignment:', randomPath)
    }
  }, [])

  const handleStartDecisions = () => {
    if (abTestPath) {
      // Track AB test click
      const clickData = {
        path: abTestPath,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      }
      console.log('AB Test Click:', clickData)
      
      // Store click data for analysis
      const existingClicks = JSON.parse(localStorage.getItem('abTestClicks') || '[]')
      existingClicks.push(clickData)
      localStorage.setItem('abTestClicks', JSON.stringify(existingClicks))
      
      navigate(abTestPath)
    }
  }

  // Function to get AB test results (for debugging)
  const getAbTestResults = () => {
    const clicks = JSON.parse(localStorage.getItem('abTestClicks') || '[]')
    const yesnoClicks = clicks.filter((click: any) => click.path === '/tools/yesno-picker').length
    const prosConsClicks = clicks.filter((click: any) => click.path === '/decision-analysis/pros-cons').length
    return { yesnoClicks, prosConsClicks, totalClicks: clicks.length }
  }

  return (
    <section className="mb-16">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Get Started in 3 Simple Steps</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Making better decisions has never been easier. Follow these simple steps to get started.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="text-center relative">
              <div className={`bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg ${step.color}`}>
                {step.icon}
              </div>
              <div className="text-2xl font-bold text-gray-400 mb-2">{index + 1}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-600">{step.description}</p>
              
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 right-0 transform translate-x-1/2">
                  <ArrowRight className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <button 
            onClick={handleStartDecisions}
            className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            <Play className="w-5 h-5" />
            <span>Start Making Decisions</span>
          </button>
          
          {/* Debug info - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-4 text-xs text-gray-500">
              <p>AB Test Path: {abTestPath}</p>
              <p>Results: {JSON.stringify(getAbTestResults())}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
} 